import { useState } from 'react'
import { Col, DatePicker, Modal, Row, Skeleton, Statistic } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { defaultDayRange, rangeToIso } from '@/shared/lib/dayRange'
import { fetchCampaignStats, type AdCampaign } from './campaignApi'

interface CampaignStatsModalProps {
  campaign: AdCampaign | null
  onClose: () => void
}

const { RangePicker } = DatePicker

/** ⚠️ TENANT zonasida: `dayjs()` brauzernikini olardi (`dayRange.ts`) */
const defaultRange = (): [Dayjs, Dayjs] => defaultDayRange(30)

/**
 * Kampaniya statistikasi.
 *
 * ⚠️ Reklama beruvchiga FAQAT SHU ko'rsatiladi — mehmon ma'lumoti (telefon,
 * MAC, ism) hech qachon (`CampaignController.stats` javadoc).
 */
export function CampaignStatsModal({ campaign, onClose }: CampaignStatsModalProps) {
  const { t } = useTranslation()
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange)

  const { from, to } = rangeToIso(range)

  const stats = useQuery({
    queryKey: queryKeys.campaigns.stats(campaign?.id ?? '', from, to),
    queryFn: () => fetchCampaignStats(campaign?.id ?? '', from, to),
    enabled: campaign !== null,
  })

  return (
    <Modal
      open={campaign !== null}
      title={campaign ? t('campaign.statsTitle', { name: campaign.name }) : ''}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
    >
      <RangePicker
        value={range}
        onChange={(value) => {
          // ⚠️ Chekkalar bu yerda TEKISLANMAYDI: `rangeToIso` ularni
          // tenant zonasidagi kun boshi va kun OXIRI (`23:59:59.999`) ga
          // aylantiradi. Ilgari bu yerda `endOf('day')` turardi, chunki
          // vaqtsiz tanlagich ikkala chekkani ham yarim tunga qo'yardi va
          // tanlangan oxirgi kun statistikadan tushib qolardi — reklama
          // beruvchiga ko'rsatiladigan raqam kam chiqardi. Endi bu ish
          // bitta joyda, brauzer zonasida emas.
          if (value?.[0] && value[1]) setRange([value[0], value[1]])
        }}
        allowClear={false}
        format="DD.MM.YYYY"
        style={{ marginBottom: 24, width: '100%' }}
      />

      {stats.isPending ? <Skeleton active paragraph={{ rows: 4 }} /> : null}

      {stats.isError ? <div>{translateError(stats.error, t)}</div> : null}

      {stats.data ? (
        /*
          ⚠️ `Row` MANFIY CHEKINISHINI YUTADIGAN O'RAM (tester-2, BUG-017).

          antd `Row gutter={[16, 16]}` ustunlar orasidagi bo'shliqni
          ustunga `padding-inline: 8px`, qatorga esa `margin-inline: -8px`
          qo'yib hosil qiladi. Modal tanasining O'ZIDA chekinish yo'q
          (`.ant-modal-content` da bor), ya'ni bu manfiy chekinish tanadan
          CHIQIB ketardi.

          O'lchangan (Staging, 1440x900, uz): `.ant-modal-body`
          `scrollWidth` = 480, `clientWidth` = 472 — 8px ortiqcha va aynan
          shu gorizontal scroll chizig'ini chiqarardi. Mazmun esa 520px
          oynaga bemalol sig'adi; tekshiruvchi «No need scroller» deb
          yozgani shu.

          O'ram +8px beradi, qator -8px oladi — yig'indi nol. Bu
          `gutter` ni olib tashlashdan afzal: bo'shliq saqlanadi.
        */
        <div style={{ paddingInline: 8 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Statistic title={t('campaign.stats.started')} value={stats.data.started} />
            </Col>
            <Col span={12}>
              <Statistic title={t('campaign.stats.completed')} value={stats.data.completed} />
            </Col>
            <Col span={12}>
              <Statistic
                title={t('campaign.stats.completionPercent')}
                value={stats.data.completionPercent}
                precision={1}
                suffix="%"
              />
            </Col>
            <Col span={12}>
              <Statistic title={t('campaign.stats.uniqueGuests')} value={stats.data.uniqueGuests} />
            </Col>
          </Row>
        </div>
      ) : null}
    </Modal>
  )
}
